import { useState } from 'react';
import type { Benutzer as BenutzerT } from '@beholder/shared';
import { api } from '../api';
import { useIch } from '../sitzung';
import { Symbol } from '../symbole';
import { Dialog, Eingabe, Feld, Knopf, Laden, Meldung, datum, useAktion, useDaten } from '../ui';

/** Admin: Konten anlegen, sperren, Startpasswort neu setzen, Admin-/Projektrechte. */
export function Benutzer() {
  const ich = useIch();
  const { daten, fehler, neuLaden } = useDaten(() => api.get<BenutzerT[]>('/api/benutzer'));
  const [dialog, setDialog] = useState<BenutzerT | 'neu' | null>(null);

  return (
    <div className="seite">
      <header className="seiten-kopf">
        <h1>Benutzer</h1>
        <Knopf art="haupt" onClick={() => setDialog('neu')}>
          {Symbol.plus} Neuer Benutzer
        </Knopf>
      </header>
      <Meldung>{fehler}</Meldung>
      {!daten && <Laden />}
      <div className="tabelle-rahmen">
        <table className="tabelle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Benutzername</th>
              <th>Rechte</th>
              <th>Status</th>
              <th>Seit</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {daten?.map((b) => (
              <tr key={b.id} className={b.aktiv ? '' : 'gedimmt'}>
                <td>
                  <strong>{b.anzeigename}</strong> {b.id === ich.id && <span className="marke">du</span>}
                </td>
                <td>@{b.benutzername}</td>
                <td>{b.istHauptadmin ? 'Hauptadmin' : b.istAdmin ? 'Admin' : b.darfProjekte ? 'Darf Projekte anlegen' : 'Mitglied'}</td>
                <td>{!b.aktiv ? 'gesperrt' : b.mussPasswortAendern ? 'Startpasswort offen' : 'aktiv'}</td>
                <td>{datum(b.erstelltAm)}</td>
                <td>
                  {(!b.istHauptadmin || b.id === ich.id) && (
                    <Knopf klein onClick={() => setDialog(b)}>
                      Bearbeiten
                    </Knopf>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dialog && (
        <BenutzerDialog
          benutzer={dialog === 'neu' ? null : dialog}
          schliessen={() => setDialog(null)}
          fertig={() => {
            setDialog(null);
            void neuLaden();
          }}
        />
      )}
    </div>
  );
}

function BenutzerDialog({ benutzer, schliessen, fertig }: { benutzer: BenutzerT | null; schliessen(): void; fertig(): void }) {
  const [f, setF] = useState({
    benutzername: benutzer?.benutzername ?? '',
    anzeigename: benutzer?.anzeigename ?? '',
    passwort: '',
    istAdmin: benutzer?.istAdmin ?? false,
    darfProjekte: benutzer?.darfProjekte ?? false,
    aktiv: benutzer?.aktiv ?? true,
  });
  const a = useAktion();

  async function speichern() {
    const r = benutzer
      ? await a.ausfuehren(() =>
          api.patch(`/api/benutzer/${benutzer.id}`, {
            anzeigename: f.anzeigename,
            istAdmin: f.istAdmin,
            darfProjekte: f.darfProjekte,
            aktiv: f.aktiv,
            ...(f.passwort ? { passwort: f.passwort } : {}),
          }),
        )
      : await a.ausfuehren(() => api.post('/api/benutzer', f));
    if (r) fertig();
  }

  return (
    <Dialog
      titel={benutzer ? `${benutzer.anzeigename} bearbeiten` : 'Neuer Benutzer'}
      offen
      schliessen={schliessen}
      fuss={
        <>
          <span className="abstand" />
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
          <Knopf art="haupt" onClick={speichern} disabled={a.laeuft || (!benutzer && (!f.benutzername || !f.passwort))}>
            Speichern
          </Knopf>
        </>
      }
    >
      <div className="stapel">
        {!benutzer && (
          <Feld titel="Benutzername" hinweis="3–32 Zeichen: a–z, 0–9, Punkt, Strich. Später nicht änderbar.">
            <Eingabe autoFocus value={f.benutzername} onChange={(e) => setF({ ...f, benutzername: e.target.value })} />
          </Feld>
        )}
        <Feld titel="Anzeigename">
          <Eingabe value={f.anzeigename} onChange={(e) => setF({ ...f, anzeigename: e.target.value })} />
        </Feld>
        <Feld
          titel={benutzer ? 'Neues Startpasswort (optional)' : 'Startpasswort'}
          hinweis="Mindestens 10 Zeichen. Wird bei der ersten Anmeldung durch ein eigenes ersetzt."
        >
          <Eingabe type="text" autoComplete="off" value={f.passwort} onChange={(e) => setF({ ...f, passwort: e.target.value })} />
        </Feld>
        <label className="schalter">
          <input type="checkbox" checked={f.darfProjekte || f.istAdmin} disabled={f.istAdmin} onChange={(e) => setF({ ...f, darfProjekte: e.target.checked })} />
          <span>Darf eigene Projekte anlegen</span>
        </label>
        <label className="schalter">
          <input type="checkbox" checked={f.istAdmin} onChange={(e) => setF({ ...f, istAdmin: e.target.checked })} />
          <span>Admin (verwaltet Benutzer, sieht und ändert alle Projekte)</span>
        </label>
        {benutzer && (
          <label className="schalter">
            <input type="checkbox" checked={!f.aktiv} onChange={(e) => setF({ ...f, aktiv: !e.target.checked })} />
            <span>Gesperrt (kann sich nicht mehr anmelden)</span>
          </label>
        )}
        <Meldung>{a.fehler}</Meldung>
      </div>
    </Dialog>
  );
}
